/**
 * Everything the repository environment page knows and does: the repository
 * list, the selected environment, the one run that sets it up, the credential
 * staging area, and the polling that keeps a run in flight honest.
 *
 * Nothing here remembers a run. The record is the server's, read back through
 * `read`, so a reload in the middle of setup shows the same page as the tab
 * that started it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  isQaEvidenceLimitsValid,
  isQaCommandTimeoutValid,
  isQaReadinessTimeoutValid,
  qaEnvironmentDefaults,
  type QaEnvironmentConfig,
  type QaEnvironmentView,
  type RepoIndex,
  type RepoCapabilityRecord,
  type RepoRelationsView,
  type RepoRelationWrite,
  type RepoRole,
} from "@yagni/shared";
import { captureUiError } from "../sentry.js";
import type { SiblingSearch } from "./EnvironmentFrom.js";
import type { EnvironmentYamlError, EnvironmentYamlView } from "./EnvironmentYaml.js";
import { isRunInFlight, runConfigFor } from "./environmentPage.js";
import { startPoll, steadyThenBackOff } from "./poll.js";

/**
 * The installation behind this repository could not be checked just now:
 * network, a GitHub 5xx, a rate limit. The repository is still listed, and the
 * same request may well succeed on the next attempt, so everything this code
 * says about it offers another go rather than sending the reader to an
 * administrator.
 */
export const VERIFICATION_FAILED = "repository_verification_failed";

/** The server refused because a run is already going (`qa/run.ts`). */
export const SETUP_IN_PROGRESS = "setup_in_progress";
const ENVIRONMENT_CONFLICT = "environment_conflict";

/**
 * What an inline ask adds to the run it starts: a credential's value, or a
 * configuration field such as an approved origin. The field counts as edited,
 * so the server stamps the revision as a person's.
 */
export interface RunOverrides {
  setEnvVars?: Record<string, string>;
  patch?: Partial<QaEnvironmentConfig>;
}

export interface Repository {
  id: string;
  owner: string;
  name: string;
  environment: QaEnvironmentView;
  /**
   * The repository index (YAG-831): what it is for, the variables it reads,
   * the checks it needs. Null until the sweep reaches it; absent from an
   * older server.
   */
  index?: RepoIndex | null;
  /**
   * What runs here (YAG-849): the record the last setup run, build or bench
   * wrote. Null until one has run; absent from an older server.
   */
  capabilities?: RepoCapabilityRecord | null;
  /** Present when the listing could not verify access to it this time. */
  warning?: typeof VERIFICATION_FAILED;
}

interface RepositoryList {
  repositories: Repository[];
  /** Ids the listing could not settle, carrying the reason for each. */
  unavailable?: { id: string; code: string }[];
}

/** A blank environment, exactly as the schema fills one in. */
export const emptyConfig: QaEnvironmentConfig = qaEnvironmentDefaults();

/** Drops the blank lines the textarea editors leave behind. */
export function cleaned(config: QaEnvironmentConfig): QaEnvironmentConfig {
  return {
    ...config,
    setupCommands: config.setupCommands.filter(Boolean),
    allowedOrigins: config.allowedOrigins.filter(Boolean),
    installationOrigins: (config.installationOrigins ?? ["https://registry.npmjs.org"]).filter(
      Boolean,
    ),
    maskSelectors: config.maskSelectors.filter(Boolean),
    // Nobody confirms test data (YAG-1066), but a server from before still
    // reads this while a deploy rolls, so a run always carries it.
    testDataConfirmed: true,
  };
}

export class EnvironmentApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The machine-readable reason, when the server named one. */
    readonly code?: string,
    /** The parsed error body, when there was one. */
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

/** A failure the same request may survive if the reader simply tries again. */
function isVerificationFailure(err: unknown): boolean {
  return err instanceof EnvironmentApiError && err.code === VERIFICATION_FAILED;
}

/** The last-resort message, for a failure that named nothing readable. */
export const REQUEST_FAILED = "The environment request failed.";
/**
 * The code a response earns when its body is not JSON, whatever its status:
 * in Sentry a mangling proxy then reads apart from an API that answered with
 * no error field at all.
 */
export const UNREADABLE_BODY = "unreadable_body";

/** A relations answer the sheet can show; anything else (an older server) hides the row. */
export function isRelationsView(value: unknown): value is RepoRelationsView {
  const view = value as Partial<RepoRelationsView> | null;
  return Boolean(view && Array.isArray(view.relations) && Array.isArray(view.unlinkedPointers));
}

export async function request<T>(
  url: string,
  body?: unknown,
  method: "POST" | "PATCH" | "PUT" = "POST",
): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...(body === undefined
      ? {}
      : {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
  });
  // Not every failure comes from the API. A proxy, a gateway, or a signed-out
  // redirect answers with HTML, and the parse error must not become the message
  // the reader sees: the status line is the honest thing left to say.
  let parsed: unknown;
  let readable = true;
  try {
    parsed = await response.json();
  } catch {
    readable = false;
  }
  const data = (parsed ?? {}) as Record<string, unknown>;
  if (!response.ok)
    throw new EnvironmentApiError(
      readable && typeof data.error === "string"
        ? data.error
        : response.statusText || REQUEST_FAILED,
      response.status,
      readable ? (typeof data.code === "string" ? data.code : undefined) : UNREADABLE_BODY,
      readable ? data : undefined,
    );
  // A 2xx that is not JSON is a proxy answering for the API, never data.
  if (!readable) throw new EnvironmentApiError(REQUEST_FAILED, response.status, UNREADABLE_BODY);
  return parsed as T;
}

export function useRepositoryEnvironment() {
  const [params, setParams] = useSearchParams();
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [listRevision, setListRevision] = useState(0);
  const [listBusy, setListBusy] = useState(true);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [view, setView] = useState<QaEnvironmentView | null>(null);
  const [config, setConfig] = useState<QaEnvironmentConfig>(emptyConfig);
  const [operationBusy, setOperationBusy] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [reinspect, setReinspect] = useState(false);
  const [statusPollStopped, setStatusPollStopped] = useState(false);
  const [statusPollRevision, setStatusPollRevision] = useState(0);
  const [secretName, setSecretName] = useState("");
  const [secretValue, setSecretValue] = useState("");
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [removed, setRemoved] = useState<string[]>([]);
  const [refreshDependencyRepoIds, setRefreshDependencyRepoIds] = useState<string[]>([]);
  const [indexBusy, setIndexBusy] = useState(false);
  const [indexError, setIndexError] = useState("");
  /** The selected repository's related repositories; undefined from a server without them. */
  const [relations, setRelations] = useState<RepoRelationsView | undefined>(undefined);
  const [relationsBusy, setRelationsBusy] = useState(false);
  const [relationsError, setRelationsError] = useState("");
  const [yamlError, setYamlError] = useState<EnvironmentYamlError | null>(null);

  const editedFields = useRef(new Set<keyof QaEnvironmentConfig>());
  const runOperation = useRef<{ signature: string; key: string } | null>(null);

  const repositoryName = params.get("repository");
  const selected =
    params.get("repo") ??
    (repositoryName
      ? repositories.find(
          (item) => `${item.owner}/${item.name}`.toLowerCase() === repositoryName.toLowerCase(),
        )?.id
      : repositories[0]?.id);
  const selection = useRef({ repo: selected, generation: 0 });
  if (selection.current.repo !== selected)
    selection.current = {
      repo: selected,
      generation: selection.current.generation + 1,
    };
  const generation = selection.current.generation;
  const repo = repositories.find((item) => item.id === selected);
  const base = `/api/qa/repositories/${selected}/environment/`;

  const report = useCallback((err: unknown) => {
    setError(err instanceof Error ? err.message : REQUEST_FAILED);
    if (isVerificationFailure(err)) setVerificationFailed(true);
    captureUiError(err, {
      area: "Settings",
      operation: "repository_environment",
    });
  }, []);

  const apply = useCallback((next: QaEnvironmentView) => {
    setView(next);
    setConfig(next.draft?.config ?? next.active?.config ?? emptyConfig);
    setDirty(false);
    setConfigured(Boolean(next.draft || next.active));
    editedFields.current.clear();
    setSecrets({});
    setRemoved([]);
    setRefreshDependencyRepoIds([]);
    setSecretValue("");
  }, []);

  useEffect(() => {
    let live = true;
    setListBusy(true);
    void request<RepositoryList>("/api/qa/repositories")
      .then((data) => {
        if (!live) return;
        // The listing reports an unverified repository twice: once inline and
        // once in `unavailable`. Either is enough to mark the row.
        const unverified = new Set(
          (data.unavailable ?? [])
            .filter((entry) => entry.code === VERIFICATION_FAILED)
            .map((entry) => entry.id),
        );
        setRepositories(
          data.repositories.map((item) =>
            item.warning || unverified.has(item.id)
              ? { ...item, warning: VERIFICATION_FAILED }
              : item,
          ),
        );
      })
      .catch((err) => {
        if (live) report(err);
      })
      .finally(() => {
        if (!live) return;
        setLoaded(true);
        setListBusy(false);
      });
    return () => {
      live = false;
    };
  }, [report, listRevision]);

  // The selected repository's related repositories (YAG-1067), read once per
  // selection. A server without the route, or any failure, hides the row.
  const indexKnown = repo?.index !== undefined;
  useEffect(() => {
    setRelations(undefined);
    setRelationsError("");
    if (!selected || !indexKnown) return;
    let live = true;
    void request<unknown>(`/api/qa/repositories/${selected}/relations`)
      .then((view) => {
        if (live && isRelationsView(view)) setRelations(view);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [selected, indexKnown]);

  useEffect(() => {
    setOperationBusy(false);
    setView(null);
    setConfig(emptyConfig);
    setSecrets({});
    setRemoved([]);
    setRefreshDependencyRepoIds([]);
    setSecretValue("");
    setSecretName("");
    setDirty(false);
    setConfigured(false);
    setReinspect(false);
    editedFields.current.clear();
    setError("");
    setIndexError("");
    setYamlError(null);
    setVerificationFailed(false);
    if (!selected) return;
    let live = true;
    void request<QaEnvironmentView>(base + "read")
      .then((data) => {
        if (live) apply(data);
      })
      .catch((err) => {
        if (live) report(err);
      });
    return () => {
      live = false;
    };
  }, [selected, base, apply, report]);

  // A run in flight: setting up, or waiting on a private sign-in, which the
  // pill reads as Needs you but is the same run, still stoppable and polled.
  const validating = isRunInFlight(view);
  const busy = operationBusy;
  /** The record of the most recent run, whichever revision it belongs to. */
  const latestRun = view?.latestRun ?? null;
  const validation = latestRun?.validation ?? null;

  useEffect(() => {
    if (!validating) return;
    setStatusPollStopped(false);
    return startPoll<QaEnvironmentView>({
      // The first read is a beat away: the POST that started this just answered.
      delayFirstAttempt: true,
      fetch: () => request<QaEnvironmentView>(base + "read"),
      // A run that has finished may have rewritten the configuration on its
      // way (the repair phase does), so the settled view is applied in full.
      onResult: (next) => (isRunInFlight(next) ? setView(next) : apply(next)),
      onError: (err, failures) => {
        report(err);
        // Access went away mid-run, so the details go with it.
        if (err instanceof EnvironmentApiError && [403, 404].includes(err.status)) {
          setView(null);
          return "stop";
        }
        if (failures >= 5) {
          setStatusPollStopped(true);
          return "stop";
        }
        return "retry";
      },
      backoff: steadyThenBackOff,
    });
  }, [validating, base, statusPollRevision, apply, report]);

  const edit = useCallback((patch: Partial<QaEnvironmentConfig>) => {
    setConfig((current) => ({ ...current, ...patch }));
    setDirty(true);
    for (const key of Object.keys(patch) as (keyof QaEnvironmentConfig)[])
      editedFields.current.add(key);
    if (Object.keys(patch).some((key) => ["target", "setupCommands", "services"].includes(key)))
      setConfigured(true);
  }, []);

  const action = useCallback(
    async (
      name: string,
      body: unknown,
      /** Says a refusal in place; true keeps it off the sheet's error line. */
      handled?: (err: unknown) => boolean,
    ): Promise<QaEnvironmentView | null> => {
      if (!selected) return null;
      setOperationBusy(true);
      setError("");
      setVerificationFailed(false);
      try {
        const next = await request<QaEnvironmentView>(base + name, body);
        if (selection.current.generation !== generation) return null;
        apply(next);
        return next;
      } catch (err) {
        if (selection.current.generation !== generation) return null;
        // Another tab (or an automatic first setup) started a run first. That
        // is the state the page should show, not an error: read it back and
        // the page says Setting up.
        if (err instanceof EnvironmentApiError && err.code === SETUP_IN_PROGRESS) {
          // The caller may still say why its request was not taken.
          handled?.(err);
          try {
            const next = await request<QaEnvironmentView>(base + "read");
            if (selection.current.generation === generation) setView(next);
          } catch (readErr) {
            if (selection.current.generation === generation) report(readErr);
          }
          return null;
        }
        if (handled?.(err)) return null;
        report(err);
        return null;
      } finally {
        if (selection.current.generation === generation) setOperationBusy(false);
      }
    },
    [selected, base, generation, apply, report],
  );

  /** Puts a fresh index row on its repository in the list. */
  const placeIndex = useCallback((repoId: string, index: RepoIndex | null) => {
    setRepositories((list) => list.map((item) => (item.id === repoId ? { ...item, index } : item)));
  }, []);

  /**
   * Reads the repository again (`POST …/index`) and puts the answer on the
   * row. A null answer means the read is still going: the row says Indexing
   * and the list is read again for whatever the server holds.
   */
  const reindex = useCallback(
    async (repoId: string) => {
      setIndexBusy(true);
      setIndexError("");
      try {
        const answer = await request<{ index: RepoIndex | null }>(
          `/api/qa/repositories/${repoId}/index`,
          {},
        );
        placeIndex(repoId, answer.index ?? null);
        if (!answer.index) setListRevision((value) => value + 1);
      } catch (err) {
        setIndexError(err instanceof Error ? err.message : REQUEST_FAILED);
        captureUiError(err, { area: "Settings", operation: "repository_reindex" });
      } finally {
        setIndexBusy(false);
      }
    },
    [placeIndex],
  );

  /** Saves a person's role (YAG-1067); null returns to what the index finds. */
  const saveRole = useCallback(
    async (repoId: string, role: RepoRole | null): Promise<boolean> => {
      setIndexBusy(true);
      setIndexError("");
      try {
        const index = await request<RepoIndex>(
          `/api/qa/repositories/${repoId}/role`,
          { role },
          "PATCH",
        );
        placeIndex(repoId, index);
        // Roles decide pointer edges: the related repositories are read again.
        void request<unknown>(`/api/qa/repositories/${repoId}/relations`)
          .then((view) => {
            if (selection.current.repo === repoId && isRelationsView(view)) setRelations(view);
          })
          .catch(() => undefined);
        return true;
      } catch (err) {
        setIndexError(err instanceof Error ? err.message : REQUEST_FAILED);
        captureUiError(err, { area: "Settings", operation: "repository_role" });
        return false;
      } finally {
        setIndexBusy(false);
      }
    },
    [placeIndex],
  );

  /** A person asserts, removes, or clears their word on one related repository. */
  const saveRelation = useCallback(
    async (repoId: string, write: RepoRelationWrite): Promise<boolean> => {
      setRelationsBusy(true);
      setRelationsError("");
      try {
        const view = await request<unknown>(
          `/api/qa/repositories/${repoId}/relations`,
          write,
          "PUT",
        );
        if (selection.current.repo === repoId && isRelationsView(view)) setRelations(view);
        return true;
      } catch (err) {
        setRelationsError(err instanceof Error ? err.message : REQUEST_FAILED);
        captureUiError(err, { area: "Settings", operation: "repository_relations" });
        return false;
      } finally {
        setRelationsBusy(false);
      }
    },
    [],
  );

  /** Saves a person's description; null (an empty box) returns to the inferred one. */
  const saveDescription = useCallback(
    async (repoId: string, description: string | null): Promise<boolean> => {
      setIndexBusy(true);
      setIndexError("");
      try {
        const index = await request<RepoIndex>(
          `/api/qa/repositories/${repoId}/description`,
          { description },
          "PATCH",
        );
        placeIndex(repoId, index);
        return true;
      } catch (err) {
        setIndexError(err instanceof Error ? err.message : REQUEST_FAILED);
        captureUiError(err, { area: "Settings", operation: "repository_description" });
        return false;
      } finally {
        setIndexBusy(false);
      }
    },
    [placeIndex],
  );

  const configValid =
    isQaEvidenceLimitsValid(config) &&
    isQaCommandTimeoutValid(config.commandTimeoutMs) &&
    config.services.every((service) => isQaReadinessTimeoutValid(service.readinessTimeoutMs));
  const credentialsPending = Boolean(secretName || secretValue);
  /**
   * An inline ask brings its own value, so a half-typed pair in the
   * credentials row does not hold it back; everything else that stops a run
   * does.
   */
  const canAsk = Boolean(selected && !busy && !validating && configValid);
  const canRun = canAsk && !credentialsPending;

  /**
   * One call. The server saves the configuration, reads the repository when it
   * has to, boots it, repairs it if it can, and records the whole attempt on
   * the revision. The page only asks and then reads.
   */
  async function run(overrides: RunOverrides = {}) {
    const asked = Boolean(overrides.setEnvVars || overrides.patch);
    if (!(asked ? canAsk : canRun)) return;
    const edited = new Set(editedFields.current);
    for (const key of Object.keys(overrides.patch ?? {}) as (keyof QaEnvironmentConfig)[])
      edited.add(key);
    const body = {
      config: cleaned({ ...config, ...overrides.patch }),
      expectedDraftId: view?.draft?.id ?? null,
      setEnvVars: { ...secrets, ...overrides.setEnvVars },
      removeEnvVars: removed,
      refreshDependencyRepoIds,
      // The server reads the repository anyway when nothing has ever been
      // saved, so this only ever asks for a re-reading.
      reinspect,
      editedFields: [...edited],
    };
    // The same ask twice is the same run: a double click, or a retry after a
    // connection dropped, must not start a second sandbox.
    const signature = JSON.stringify({ base, ...body });
    if (runOperation.current?.signature !== signature)
      runOperation.current = { signature, key: crypto.randomUUID() };
    await action("run", { ...body, operationKey: runOperation.current.key });
  }

  /**
   * Validate now: something saved (the draft, else the Working revision),
   * nothing running, and nothing unsaved. The saved text runs, and the settled
   * run's view replaces the form, so edits or staged values would be lost.
   */
  const checkHeldByEdits = dirty || credentialsPending;
  const canCheck = Boolean(
    selected && !busy && !validating && !checkHeldByEdits && (view?.draft || view?.active),
  );

  /**
   * Validate now (`POST …/check`): the server runs the saved environment once,
   * unchanged and without repair, and answers with the revision it runs. The
   * view is read back so the page says Setting up and keeps polling.
   */
  async function check() {
    if (!canCheck) return;
    const startedFor = generation;
    setOperationBusy(true);
    setError("");
    setVerificationFailed(false);
    try {
      await request<{ revisionId: string }>(base + "check", {});
      const next = await request<QaEnvironmentView>(base + "read");
      if (selection.current.generation === startedFor) setView(next);
    } catch (err) {
      if (selection.current.generation !== startedFor) return;
      if (err instanceof EnvironmentApiError && err.code === SETUP_IN_PROGRESS) {
        // Another run got there first: read it back and the page says so.
        try {
          const next = await request<QaEnvironmentView>(base + "read");
          if (selection.current.generation === startedFor) setView(next);
        } catch (readErr) {
          report(readErr);
        }
        return;
      }
      report(err);
    } finally {
      if (selection.current.generation === startedFor) setOperationBusy(false);
    }
  }

  /** The environment as YAML; undefined from a server that does not send it yet. */
  const yaml: EnvironmentYamlView | undefined = view?.yaml;

  /**
   * Saves the YAML as the declared environment and runs setup on it, the way
   * the form's Run does. A refusal that names a line stays beside the text.
   */
  async function saveYaml(text: string): Promise<boolean> {
    if (!selected || busy || validating) return false;
    setYamlError(null);
    const next = await action(
      "yaml",
      { text, expectedDraftId: view?.draft?.id ?? null, operationKey: crypto.randomUUID() },
      (err) => {
        if (!(err instanceof EnvironmentApiError)) return false;
        if (err.code === SETUP_IN_PROGRESS) {
          setYamlError({
            message:
              "A setup run is in progress. Your text was not saved; it stays in the box until that run ends.",
          });
          return true;
        }
        if (err.code === ENVIRONMENT_CONFLICT) {
          setYamlError({
            message:
              "The environment changed since you opened it. Reload, then paste your text again.",
          });
          return true;
        }
        if (![400, 413].includes(err.status)) return false;
        const details = err.details ?? {};
        setYamlError({
          message: typeof details.message === "string" ? details.message : err.message,
          ...(typeof details.line === "number" ? { line: details.line } : {}),
        });
        return true;
      },
    );
    return next !== null;
  }

  /** Reads the repository again and runs on what it infers, leaving the declared YAML behind. */
  async function resetToInferred() {
    if (!selected || busy || validating) return;
    setYamlError(null);
    await action("run", {
      config: cleaned(view?.draft?.config ?? view?.active?.config ?? emptyConfig),
      expectedDraftId: view?.draft?.id ?? null,
      setEnvVars: {},
      removeEnvVars: [],
      refreshDependencyRepoIds: [],
      reinspect: true,
      editedFields: [],
      operationKey: crypto.randomUUID(),
    });
  }

  return {
    repositories,
    repositoryName,
    /**
     * The selected repository's index row: null while it has none, undefined
     * from a server that does not send the field.
     */
    index: repo?.index,
    reindex,
    saveDescription,
    saveRole,
    /** A re-index, description or role save is in flight. */
    indexBusy,
    indexError,
    /** The selected repository's related repositories; undefined hides the row. */
    relations,
    saveRelation,
    relationsBusy,
    relationsError,
    loaded,
    selected,
    repo,
    view,
    latestRun,
    config,
    /** The configuration the checklist is read against (`runConfigFor`). */
    runConfig: runConfigFor(view, latestRun, config),
    error,
    busy,
    operationBusy,
    validating,
    validation,
    dirty,
    configured,
    reinspect,
    setReinspect,
    statusPollStopped,
    /**
     * Access to the selected repository could not be checked, either when the
     * list was drawn or when an action tried it. Both clear on a retry, so the
     * page offers one rather than sending the reader to an administrator.
     */
    verificationRetryable: verificationFailed || Boolean(repo?.warning),
    /**
     * The last failure was that verification, so the notice above already
     * says it and the plain error line would only repeat it.
     */
    verificationFailed,
    /** True while the "Try again" refetch is in flight. */
    listBusy,
    retryVerification: () => {
      setVerificationFailed(false);
      setError("");
      setListRevision((value) => value + 1);
    },
    refreshStatus: () => setStatusPollRevision((value) => value + 1),
    canRun,
    /** An inline ask may start its run (see `canAsk`). */
    canAsk,
    /**
     * A credential name or value is typed but not added. It holds the run
     * back, and the run's help line says so.
     */
    credentialsPending,
    select: (repoId: string) => setParams({ repo: repoId }),
    edit,
    run,
    /** Validate now may start (`canCheck`). */
    canCheck,
    /** Unsaved edits or staged values hold Validate now back. */
    checkHeldByEdits,
    check,
    /** The repository's declared systems; absent from an older server. */
    systems: view?.systems,
    /** The environment as YAML (`view.yaml`); undefined hides the section. */
    yaml,
    /** Why the last YAML save was refused, with its line when the server knew it. */
    yamlError,
    saveYaml,
    resetToInferred,
    stop: () => (view?.draft ? action("cancel", { revisionId: view.draft.id }) : null),
    /** Put the newest run's revision to work in place of the current one. */
    switchTo: () =>
      latestRun
        ? action("activate", {
            revisionId: latestRun.revisionId,
            expectedActiveId: view?.activeRevisionId ?? null,
          })
        : null,
    reread: () =>
      request<QaEnvironmentView>(base + "read")
        .then((next) => {
          if (selection.current.generation === generation) setView(next);
        })
        .catch(report),
    stagedSecretNames: Object.keys(secrets),
    removed,
    secretName,
    secretValue,
    setSecretName,
    setSecretValue,
    toggleRemoved: (name: string) => {
      setRemoved((list) =>
        list.includes(name) ? list.filter((item) => item !== name) : [...list, name],
      );
      setDirty(true);
    },
    addSecret: () => {
      setSecrets((current) => ({ ...current, [secretName]: secretValue }));
      setRemoved((list) => list.filter((name) => name !== secretName));
      setSecretName("");
      setSecretValue("");
      setDirty(true);
    },
    /**
     * Stages several write-only credentials at once: a pasted `.env`, or a
     * plaintext variable moved into Credentials. Nothing is saved until the
     * run is.
     */
    stageSecrets: (values: Record<string, string>) => {
      const names = Object.keys(values);
      if (!names.length) return;
      setSecrets((current) => ({ ...current, ...values }));
      setRemoved((list) => list.filter((name) => !names.includes(name)));
      setDirty(true);
    },
    refreshDependency: (repoId: string) => {
      setRefreshDependencyRepoIds((ids) => [...new Set([...ids, repoId])]);
      setDirty(true);
    },
    /**
     * Find again (YAG-1064): which sibling's compose file defines this
     * repository's environment, read now. `found` is null when none names
     * it, and `walk` says whether the server read every repository. The
     * answer goes into the draft only when the row applies it.
     */
    findSibling: async (): Promise<SiblingSearch> => {
      if (!selected) return { found: null };
      return request<SiblingSearch>(base + "detect-sibling", {});
    },
  };
}
